import {
  Avatar,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { EventDetailCollectionBoxFragment } from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import { useRouter } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'
import { useAnalytics } from '@/lib/analytics'

export type StoreCardProps = {
  eventCollection: EventDetailCollectionBoxFragment
}

export const StoreCard: Organism<StoreCardProps> = ({ eventCollection }) => {
  const { generatePath, routerPages } = useRouter()
  const { t } = useTranslation('eventDetail')
  const { analytics } = useAnalytics()

  return (
    <OrganismFrame>
      <Card sx={{ borderRadius: 2 }}>
        <CardActionArea
          href={generatePath(routerPages.ShokufesEventCollectionDetail, {
            collectionId:
              eventCollection.collectionId ??
              eventCollection.collection.id ??
              '#',
            eventId: eventCollection.eventId,
          })}
          onClick={() => {
            analytics?.track('click_select', {
              applicationKey: config.applicationKey,
              eventId: eventCollection.eventId,
              collectionId: eventCollection.collection.id,
              artistId: eventCollection.collection.artist.id,
              name: 'eventCollection',
            })
          }}
        >
          <CardMedia
            component="img"
            image={eventCollection.collection.thumbnail.uri ?? ''}
            alt={eventCollection.collection.title}
            sx={{ height: 160 }}
          />
          <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
            <PickupLabel
              isEventPickup={!!eventCollection.collection.isEventPickup}
            />

            <Typography variant="caption">
              {t('itemCount', { count: eventCollection.collection.itemCount })}
            </Typography>
            <Typography variant="h6" sx={{ pt: 1, ...styleSets.lineCamp(2) }}>
              {eventCollection.collection.title}
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ pt: 1 }}
            >
              <Avatar
                src={
                  eventCollection.collection.artist?.avatar?.smallUri ??
                  eventCollection.collection.artist?.avatar?.uri ??
                  ''
                }
                sx={{ width: 24, height: 24 }}
              />
              <Typography variant="overline" color="text.secodary">
                {eventCollection.collection.artist?.displayName}
              </Typography>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </OrganismFrame>
  )
}

export const PickupLabel = ({ isEventPickup }: { isEventPickup: boolean }) => {
  const { t } = useTranslation('eventDetail')

  if (!isEventPickup) return null

  return (
    <Chip
      size="small"
      sx={{
        mr: 2,
        height: 20,
        borderRadius: '2px',
        minWidth: 40,
        '.MuiChip-label': {
          px: 1,
        },
        bgcolor: 'primary.main',
      }}
      label={
        <Typography color="white" variant="overline">
          {t('pickup')}
        </Typography>
      }
    />
  )
}
